import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, User, LogOut, LayoutDashboard, FileText, Users, Search } from "lucide-react";
import axios from "axios";

const Navbar = () => {
  const { user, setUser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Role-based navigation
  const getNavLinks = () => {
    if (!user) {
      // Not logged in - show login/register only
      return [];
    }
    
    if (user.role === "admin") {
      return [
        { href: "/admin", label: "Admin Dashboard" },
        { href: "/directory", label: "Directory" },
      ];
    }
    
    if (user.role === "committee") {
      return [
        { href: "/committee", label: "Review Portal" },
        { href: "/directory", label: "Directory" },
      ];
    }
    
    // Regular member or applicant
    return [
      { href: "/dashboard", label: "My Dashboard" },
      { href: "/directory", label: "Directory" },
    ];
  };

  const navLinks = getNavLinks();
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("session_token");
      setUser(null);
      navigate("/login");
    }
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/login"} className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_cpsi-directory/artifacts/ckmcoulj_CPSI%20Logo.png" 
              alt="CPSI Logo"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-xl text-slate-900">CPSI</span>
              <span className="text-xs text-slate-500 block -mt-1">Member Portal</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          {navLinks.length > 0 && (
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(link.href)
                      ? "text-teal-700 bg-teal-50"
                      : "text-slate-600 hover:text-teal-700 hover:bg-slate-50"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-teal-700" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">
                      {user.name || user.email?.split("@")[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-slate-900">{user.name || "Member"}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                    <p className="text-xs text-teal-600 capitalize">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/directory" className="flex items-center gap-2 cursor-pointer" data-testid="nav-directory">
                      <Search className="w-4 h-4" />
                      Member Directory
                    </Link>
                  </DropdownMenuItem>
                  {(user.role === "committee" || user.role === "admin") && (
                    <DropdownMenuItem asChild>
                      <Link to="/committee" className="flex items-center gap-2 cursor-pointer" data-testid="nav-committee">
                        <FileText className="w-4 h-4" />
                        Review Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="nav-admin">
                        <Users className="w-4 h-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600 cursor-pointer"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600" data-testid="nav-login">
                    Login
                  </Button>
                </Link>
                <Link to="/join">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="nav-join">
                    Register
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            {navLinks.length > 0 && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && navLinks.length > 0 && (
        <div className="md:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 text-sm font-medium rounded-md ${
                  isActive(link.href)
                    ? "text-teal-700 bg-teal-50"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
